import { Button } from "primereact/button";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from "react-i18next";
import PlanActions from "../../Redux/transition/plan/plan.reducer";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toast } from "primereact/toast";
import { createHandleChangeApostrophe, descriptionDotHandleChange, removeTrailingSpacesAndDashes, createGraceRemindervalidation } from "../../Components/CleanInputs";

const PlanEdit = ({ onHide, tier, setTrigger, trigger }) => {

  const [formData, setFormData] = useState({
    name: tier.name,
    description: tier.description,
    graceDays: tier.graceDays != null && tier.graceDays !== 0 ? String(tier.graceDays) : "",
    reminderDays: tier.reminderDays != null ? String(tier.reminderDays) : "",
  });

  const disableDays = (tier.billingPeriod &&
    ["ONCE", "DAILY", "DAILY_WITH_SATURDAYS", "DAILY_WITH_WEEKENDS", "WEEKLY"].includes(tier.billingPeriod));

  const [errors, setFieldErrors] = useState({
    name: '',
    description: '',
    graceDays: '',
    reminderDays: ''
  });

  const { t } = useTranslation("common");
  const commonArgs = [setFormData, setFieldErrors, t];
  const [successToastVisible, setSuccessToastVisible] = useState(false);

  const handleChange = createHandleChangeApostrophe(...commonArgs);
  const handleDescriptionChange = descriptionDotHandleChange(...commonArgs);
  const handleChangeGraceReminderChange = createGraceRemindervalidation(...commonArgs)

  const [state, setState] = useState({
    submit: false
  });

  const toastPlan = useRef(null);

  const {
    control,
    handleSubmit,
    reset
  } = useForm({
    defaultValues: {
      name: tier.name,
      description: tier.description,
      graceDays: tier.graceDays,
      reminderDays: tier.reminderDays,
      flex: tier.flex,
      policy: tier.policy
    }
  });

  const { updating, errorUpdating } = useSelector((state) => state.plans)
  const defMembership = useSelector((state) => state.defMembership.defMembership);

  const dispatch = useDispatch()
  const updateTier = useCallback((planId, plan) => { dispatch(PlanActions.planUpdateRequest(planId, plan)) }, [dispatch])
  const getActivePlanGroups = useCallback((businessId) => { dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)) }, [dispatch]);

  const businessPurpose = defMembership?.business.purpose;
  const graceDaysPurpose = businessPurpose === "SERVICES" ? t("plan.payment") : t("plan.donating");
  const reminderDaysPurpose = businessPurpose === "SERVICES" ? t("plan.payment") : t("plan.donating");

  useEffect(() => {
    if (state.submit) {
      if (!updating) {
        if (!errorUpdating) {
          showSuccess();
        } else {
          showError();
        }
        setState((state) => ({ ...state, submit: false }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorUpdating, updating, state.submit]);

  useEffect(() => {
    setFormData({
      name: tier.name,
      description: tier.description,
      graceDays: tier.graceDays != null && tier.graceDays !== 0 ? String(tier.graceDays) : "",
      reminderDays: tier.reminderDays != null && tier.reminderDays !== 0 ? String(tier.reminderDays) : "",
    });
  }, [tier.id, tier.name, tier.description, tier.graceDays, tier.reminderDays]);

  const showSuccess = () => {
    setSuccessToastVisible(true);
    toastPlan.current?.show({
      severity: 'success',
      summary: t("success"),
      detail: t("plan.edit_success"),
      life: 2000
    });
  }
  const showError = () => { toastPlan.current?.show({ severity: 'error', summary: t("failed"), detail: t("product.edit_error"), life: 3000 }); }

  const onSubmit = () => {
    const graceDays = formData.graceDays !== "" && formData.graceDays != null ? Number(formData.graceDays) : 0;
    const reminderDays = formData.reminderDays !== "" && formData.reminderDays != null ? Number(formData.reminderDays) : 0;
    const remindAllowed = reminderDays > 0;

    updateTier(tier.id, {
      tier: {
        ...tier,
        graceDays,
        reminderDays,
        remindAllowed,
      },
      id: tier.id,
      name: removeTrailingSpacesAndDashes(formData.name),
      description: removeTrailingSpacesAndDashes(formData.description),
      flex: tier.flex,
      policy: tier.policy,
      graceDays,
      reminderDays,
      remindAllowed,
    });

    if (!state.submit) {
      setState({ ...state, submit: true });
    }
  };

  const fetchActive = () => {
    getActivePlanGroups(defMembership.business.id);
  };

  return (
    <>
      <Dialog
        header={t("products.edit_plan_info")}
        visible={!!tier.id}
        style={{ width: "355px" }}
        onHide={onHide}
        className="tier_dialog" id="tier_dialog"
      >
        <Toast
          className="p-toast p-toast-container p-toast-item p-toast-message p-toast-title "
          ref={toastPlan}
          onRemove={({ severity }) => {
            setSuccessToastVisible(false);
            if (severity === "success") {
              reset()
              onHide({})
              setTrigger(!trigger)
              fetchActive()
            }
          }}
        />
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid mt-5">
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <div className="flex flex-column gap-2 mb-2" style={{ padding: "0px 10px 0px 10px" }}>
                  <label className="plan_edit_labels" >{t("plan.name")}</label>
                  <InputText
                    style={{ height: "3rem" }}
                    defaultValue={tier.name}
                    className={classNames({ 'p-invalid': fieldState.error })}
                    value={formData.name}
                    onChange={handleChange('name')}
                    maxLength={50}
                  />
                </div>
                {errors.name && <p className="character_errors_edits">{errors.name}</p>}
              </>
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <div className="flex flex-column gap-2 mb-2 mt-4" style={{ padding: "0px 10px 0px 10px" }}>
                  <label className="plan_edit_labels" >{t("plan.description")}</label>
                  <InputText
                    style={{ height: "3rem" }}
                    defaultValue={tier.description}
                    className={classNames({ 'p-invalid': fieldState.error })}
                    value={formData.description}
                    onChange={handleDescriptionChange('description')}
                    maxLength={100}
                  />
                </div>
                {errors.description && <p className="character_errors_edits">{errors.description}</p>}
              </>
            )}
          />

          <Controller
            name="graceDays"
            control={control}
            render={({ field, fieldState }) => (
              <>
                {!disableDays && (
                  <>
                    <div className="flex flex-column gap-2 mb-2 mt-4" style={{ padding: "0px 10px 0px 10px" }}>
                      <label className="plan_edit_labels">{t("plan.grace_period")}</label>
                      <InputText
                        style={{ height: "3rem" }}
                        defaultValue={tier.graceDays || ""}
                        className={classNames({ 'p-invalid': fieldState.error })}
                        value={formData.graceDays ?? ""}
                        onChange={handleChangeGraceReminderChange('graceDays')}
                        maxLength={1}
                      />
                    </div>
                    {errors.graceDays && <p className="character_errors_edits">{errors.graceDays}</p>}
                    <div className="text-xs" style={{ marginTop: "0.7rem", paddingLeft: "10px" }}>
                      <Trans
                        i18nKey="plan.graceDays_hint"
                        values={{ graceDays: graceDaysPurpose }}
                        components={{
                          1: <span className="hints" />,
                          2: <span className="bolder_hints" />,
                          3: <span className="bolder_hints" />,
                          4: <span className="bolder_hints" />,
                          5: <span className="hints" />,
                          6: <span className="bolder_hints" />
                        }}
                      />
                      <span className="bolder_hints" style={{ display: "block", marginTop: "0.3rem" }}>
                        {t("plan.leave_blank_to_remove_grace")}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          />

          <Controller
            name="reminderDays"
            control={control}
            render={({ field, fieldState }) => (
              <>
                {!disableDays && (
                  <>
                    <div className="flex flex-column gap-2 mb-2 mt-4" style={{ padding: "0px 10px 0px 10px" }}>
                      <label className="plan_edit_labels">{t("plan.reminder")}</label>
                      <InputText
                        style={{ height: "3rem" }}
                        defaultValue={tier.reminderDays || ""}
                        className={classNames({ 'p-invalid': fieldState.error })}
                        value={formData.reminderDays ?? ""}
                        onChange={handleChangeGraceReminderChange('reminderDays')}
                        maxLength={1}
                      />
                    </div>
                    {errors.reminderDays && <p className="character_errors_edits">{errors.reminderDays}</p>}
                    <div className="text-xs" style={{ marginTop: "0.7rem", paddingLeft: "10px" }}>
                      <Trans
                        i18nKey="plan.reminderDays_hint"
                        values={{ reminderDays: reminderDaysPurpose }}
                        components={{
                          1: <span className="hints" />,
                          2: <span className="bolder_hints" />,
                          3: <span className="bolder_hints" />,
                          4: <span className="bolder_hints" />,
                          5: <span className="hints" />,
                          6: <span className="bolder_hints" />
                        }}
                      />
                      <span className="bolder_hints" style={{ display: "block", marginTop: "0.3rem" }}>
                        {t("plan.leave_blank_to_remove_reminder")}
                      </span>
                    </div>
                  </>
                )}
              </>
            )}
          />
          <div>
            <div className="edit_plan_footer">
              <Button id="footer-btn-cancel"
                type="reset"
                label={t("cancel")}
                icon="pi pi-times"
                onClick={onHide}
              />
              <Button
                type="submit"
                disabled={updating || successToastVisible}
                loading={updating}
                label={t("common.save")}
                id="rename-btn"
              />
            </div>
          </div>
        </form>
      </Dialog >
    </>
  );
};
export default PlanEdit;